import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  X,
  Package,
  Building2,
  Tag,
  FileText,
  Plus,
  Trash2,
  Eye,
  Edit,
  Save,
  AlertCircle,
  CheckCircle,
  Info,
  Search,
  AlertTriangle,
  ClipboardCheck,
  Calendar
} from 'lucide-react';
import { scoreSimilaridadeInsumo, normalizarTextoMp } from '../utils/insumoBusca';
import {
  onChangeSingleLineText,
  onChangeMultiLineText,
  sanitizeSingleLineText,
  sanitizeMultiLineText,
  sanitizeProductSavePayload,
} from '../utils/textSanitize';

/** Agrupa documentos por nome de categoria e une subcategorias (sem duplicar). */
function mergeCategoriasMpPorNome(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = String(row.categoria || '').trim();
    if (!key) continue;
    const subsIn = Array.isArray(row.subcategorias) ? row.subcategorias : [];
    if (!map.has(key)) {
      map.set(key, {
        id: row.id,
        categoria: key,
        subcategorias: subsIn.map((s) => String(s).trim()).filter(Boolean),
      });
    } else {
      const prev = map.get(key);
      const set = new Set([...prev.subcategorias, ...subsIn.map((s) => String(s).trim()).filter(Boolean)]);
      prev.subcategorias = [...set];
    }
  }
  return [...map.values()].sort((a, b) =>
    a.categoria.localeCompare(b.categoria, 'pt-BR', { sensitivity: 'base' })
  );
}

/** Textos por tipo de insumo (empresa). Base global segue o mesmo bloco “ingrediente” nos rótulos. */
const INSUMO_UI = {
  ingrediente: {
    singular: 'Ingrediente',
    novoItem: 'Novo Ingrediente',
    editarItem: 'Editar Ingrediente',
    verItem: 'Visualizar Ingrediente',
    nomeCampo: 'Nome do Ingrediente',
    placeholderNome: 'Digite o nome do ingrediente',
    obsPlaceholder: 'Observações sobre o ingrediente...',
    verificacaoTab: 'Descreva o ingrediente',
    verificacaoIntro:
      'Descreva o ingrediente que precisa. Vamos mostrar cadastros parecidos para você conferir se já existe algum com nome ou sinônimo parecido (evita duplicatas por grafia diferente).',
    verificacaoLabel: 'Descreva o ingrediente que deseja criar *',
    placeholderVerificacao: 'Ex.: farinha de carne e ossos 50% proteína',
    verificacaoListaTitulo: 'Possíveis ingredientes já cadastrados',
    verificacaoSemResultado:
      'Nenhum cadastro com nome ou sinônimo muito parecido foi encontrado. Ainda assim confira na lista geral de ingredientes antes de gravar.',
    verificacaoConfirmacao: 'preciso cadastrar um ingrediente novo',
    verificacaoErroBusca: 'Não foi possível buscar ingredientes. Tente novamente.',
    categoriasAvisoTipo: 'ingrediente',
    categoriasAvisoParam: 'tipo Ingrediente',
    tabBloqueadaHint: 'Conclua primeiro a etapa inicial de verificação e confirme que é um cadastro novo.',
    similarListaHint: 'ingredientes',
  },
  embalagem: {
    singular: 'Embalagem',
    novoItem: 'Nova Embalagem',
    editarItem: 'Editar Embalagem',
    verItem: 'Visualizar Embalagem',
    nomeCampo: 'Nome da Embalagem',
    placeholderNome: 'Digite o nome ou descrição da embalagem',
    obsPlaceholder: 'Observações sobre a embalagem...',
    verificacaoTab: 'Descreva a embalagem',
    verificacaoIntro:
      'Descreva a embalagem que precisa. Vamos mostrar cadastros parecidos para você conferir se já existe alguma com nome ou sinônimo parecido (evita duplicatas por grafia diferente).',
    verificacaoLabel: 'Descreva a embalagem que deseja criar *',
    placeholderVerificacao: 'Ex.: saco laminado 25 kg, bobina PEAD…',
    verificacaoListaTitulo: 'Possíveis embalagens já cadastradas',
    verificacaoSemResultado:
      'Nenhum cadastro com nome muito parecido foi encontrado. Ainda assim confira na lista geral de embalagens antes de gravar.',
    verificacaoConfirmacao: 'preciso cadastrar uma embalagem nova',
    verificacaoErroBusca: 'Não foi possível buscar embalagens. Tente novamente.',
    categoriasAvisoTipo: 'embalagem',
    categoriasAvisoParam: 'tipo Embalagem',
    tabBloqueadaHint:
      'Conclua primeiro a etapa inicial de verificação e confirme que é um cadastro novo.',
    similarListaHint: 'embalagens',
  },
  servico: {
    singular: 'Serviço',
    novoItem: 'Novo Serviço',
    editarItem: 'Editar Serviço',
    verItem: 'Visualizar Serviço',
    nomeCampo: 'Nome do serviço',
    placeholderNome: 'Ex.: Limpeza da caixa de água, Dedetização…',
    obsPlaceholder:
      'Detalhes sobre escopo, fornecedor habitual, normas aplicáveis…',
    verificacaoTab: 'Serviço',
    verificacaoIntro: '',
    verificacaoLabel: '',
    placeholderVerificacao: '',
    verificacaoListaTitulo: '',
    verificacaoSemResultado: '',
    verificacaoConfirmacao: '',
    verificacaoErroBusca: '',
    categoriasAvisoTipo: 'serviço',
    categoriasAvisoParam: 'tipo Serviço',
    tabBloqueadaHint: '',
    similarListaHint: 'serviços',
  },
  limpeza_conservacao: {
    singular: 'Limpeza e conservação',
    novoItem: 'Novo item (limpeza e conservação)',
    editarItem: 'Editar item',
    verItem: 'Visualizar item',
    nomeCampo: 'Nome do item',
    placeholderNome: 'Ex.: Detergente neutro, desinfetante, pano de limpeza…',
    obsPlaceholder: 'Observações sobre o item de limpeza ou conservação…',
    verificacaoTab: 'Descreva o item',
    verificacaoIntro:
      'Descreva o item que precisa. Vamos mostrar cadastros parecidos para você conferir se já existe algum com nome ou sinônimo parecido (evita duplicatas por grafia diferente).',
    verificacaoLabel: 'Descreva o item que deseja criar *',
    placeholderVerificacao: 'Ex.: álcool 70%, sabonete líquido…',
    verificacaoListaTitulo: 'Possíveis itens já cadastrados',
    verificacaoSemResultado:
      'Nenhum cadastro com nome muito parecido foi encontrado. Ainda assim confira na lista geral antes de gravar.',
    verificacaoConfirmacao: 'preciso cadastrar um item novo',
    verificacaoErroBusca: 'Não foi possível buscar itens. Tente novamente.',
    categoriasAvisoTipo: 'Limpeza e conservação',
    categoriasAvisoParam: 'tipo Limpeza e conservação',
    tabBloqueadaHint:
      'Conclua primeiro a etapa inicial de verificação e confirme que é um cadastro novo.',
    similarListaHint: 'itens de limpeza e conservação',
  },
};

/** Valores canônicos gravados em `frequencia_execucao` no Firestore */
const FREQUENCIA_EXECUCAO_OPCOES = [
  { value: 'diaria', label: 'Diária' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'quinzenal', label: 'Quinzenal' },
  { value: 'mensal', label: 'Mensal' },
  { value: 'bimestral', label: 'Bimestral' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'sob_demanda', label: 'Sob demanda / quando necessário' },
];

const ProductModal = ({
  isOpen,
  onClose,
  mode,
  product,
  onSave,
  isGlobal = false,
  tipoInsumo = 'ingrediente',
}) => {
  const { getEmpresaId } = useAuth();

  const labels = useMemo(() => {
    if (isGlobal) return INSUMO_UI.ingrediente;
    return INSUMO_UI[tipoInsumo] || INSUMO_UI.ingrediente;
  }, [isGlobal, tipoInsumo]);

  const patchSingleLine = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: onChangeSingleLineText(e.target.value) }));
  };

  const blurSingleLine = (field) => () => {
    setFormData((prev) => ({
      ...prev,
      [field]: sanitizeSingleLineText(prev[field] || ''),
    }));
  };

  const patchMultiLine = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: onChangeMultiLineText(e.target.value) }));
  };

  const blurMultiLine = (field) => () => {
    setFormData((prev) => ({
      ...prev,
      [field]: sanitizeMultiLineText(prev[field] || ''),
    }));
  };

  // Debug: Log do modo recebido
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 ProductModal aberto:', { mode, isGlobal, productId: product?.id, tipoInsumo });
    }
  }, [isOpen, mode, isGlobal, product, tipoInsumo]);
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    nome: '',
    categoria: '',
    subcategoria: '',
    frequencia_execucao: '',
    industria: '',
    unidade_medida: 'kg',
    estoque_minimo: 0,
    status: 'ativo',
    observacoes: '',
    sinonimos: [],
    notas: '',
    parametros: []
  });

  // Estados de controle
  const [activeTab, setActiveTab] = useState('basico');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /** Ingredientes/embalagens: verificação na criação. Serviços: fluxo direto no formulário. */
  const precisaVerificacaoMp =
    mode === 'create' && !isGlobal && tipoInsumo !== 'servico';
  const [cadastroMpDesbloqueado, setCadastroMpDesbloqueado] = useState(true);
  const [textoDescricaoMp, setTextoDescricaoMp] = useState('');
  const [similaresMp, setSimilaresMp] = useState([]);
  const [loadingSimilares, setLoadingSimilares] = useState(false);
  const [erroSimilares, setErroSimilares] = useState('');
  const [confirmacaoNovaMp, setConfirmacaoNovaMp] = useState(false);

  /** Categorias parametrizadas (tipo matéria-prima) — coleção Categorias_Insumos */
  const [paramCategoriasMp, setParamCategoriasMp] = useState([]);
  const [loadingParamCategoriasMp, setLoadingParamCategoriasMp] = useState(false);
  const [errorParamCategoriasMp, setErrorParamCategoriasMp] = useState('');

  // Estados para arrays dinâmicos
  const [newSinonimo, setNewSinonimo] = useState('');

  const paramRowSelecionada = useMemo(() => {
    const cat = String(formData.categoria || '').trim();
    if (!cat) return null;
    return paramCategoriasMp.find((r) => r.categoria === cat) || null;
  }, [formData.categoria, paramCategoriasMp]);

  const subcategoriasOpcoesMp = useMemo(() => {
    const raw = paramRowSelecionada?.subcategorias;
    if (!Array.isArray(raw)) return [];
    return [...new Set(raw.map((s) => String(s).trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
    );
  }, [paramRowSelecionada]);

  // Inicializar formulário quando produto muda
  useEffect(() => {
    if (product) {
      console.log('🔍 Produto recebido no modal:', product);
      console.log('🔍 Parâmetros do produto:', product.parametros);
      
      // ✅ Função auxiliar para garantir que parâmetros tenham campo 'padrao'
      const normalizarParametros = (params) => {
        if (!params || !Array.isArray(params)) return [];
        return params.map(param => ({
          ...param,
          padrao: param.padrao || 'Químico' // ✅ Default para parâmetros antigos
        }));
      };
      
      if (isGlobal) {
        // Produto da base global (produtos_regulamais)
        setFormData({
          nome: sanitizeSingleLineText(product.nome_produto || ''),
          categoria: product.categoria || '',
          subcategoria: product.subcategoria || '',
          frequencia_execucao: product.frequencia_execucao || '',
          industria: product.industria || '',
          unidade_medida: product.unidade_medida || 'kg',
          estoque_minimo:
            product.estoque_minimo != null && product.estoque_minimo !== ''
              ? Number(product.estoque_minimo)
              : 0,
          status: 'ativo',
          observacoes: Array.isArray(product.observacoes_gerais) 
            ? product.observacoes_gerais.join('\n') 
            : (product.observacoes_gerais || ''),
          sinonimos: product.sinonimos || [],
          notas: Array.isArray(product.notas) 
            ? product.notas.join('\n') 
            : (product.notas || ''),
          parametros: normalizarParametros(product.parametros_qualidade || product.parametros) // ✅ NORMALIZADO: Aceita ambos durante migração
        });
      } else {
        // Produto da empresa
        setFormData({
          nome: sanitizeSingleLineText(product.nome || ''),
          categoria: product.categoria || '',
          subcategoria: product.subcategoria || '',
          frequencia_execucao: product.frequencia_execucao || '',
          industria: product.industria || '',
          unidade_medida: product.unidade_medida || 'kg',
          estoque_minimo:
            product.estoque_minimo != null && product.estoque_minimo !== ''
              ? Number(product.estoque_minimo)
              : 0,
          status: product.status || 'ativo',
          observacoes: sanitizeMultiLineText(product.observacoes || ''),
          sinonimos: product.sinonimos || [],
          notas: sanitizeMultiLineText(product.notas || ''),
          parametros: normalizarParametros(product.parametros_qualidade || product.parametros) // ✅ NORMALIZADO: Aceita ambos durante migração
        });
      }
    } else {
      // Novo produto
      const isServ = tipoInsumo === 'servico';
      setFormData({
        nome: '',
        categoria: '',
        subcategoria: '',
        frequencia_execucao: '',
        industria: 'petfood',
        unidade_medida: isServ ? 'servico' : 'kg',
        estoque_minimo: 0,
        status: 'ativo',
        observacoes: '',
        sinonimos: [],
        notas: '',
        parametros: []
      });
    }
  }, [product, isGlobal, tipoInsumo]);

  useEffect(() => {
    if (!isOpen) return;
    if (precisaVerificacaoMp) {
      setCadastroMpDesbloqueado(false);
      setActiveTab('verificacao');
      setTextoDescricaoMp('');
      setSimilaresMp([]);
      setErroSimilares('');
      setConfirmacaoNovaMp(false);
    } else {
      setCadastroMpDesbloqueado(true);
      setActiveTab((t) => (t === 'verificacao' ? 'basico' : t));
    }
  }, [isOpen, mode, isGlobal, tipoInsumo]);

  useEffect(() => {
    if (!isOpen || isGlobal) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingParamCategoriasMp(true);
        setErrorParamCategoriasMp('');
        const q = query(
          collection(db, 'Categorias_Insumos'),
          where('tipo_insumo', '==', tipoInsumo)
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setParamCategoriasMp(mergeCategoriasMpPorNome(rows));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setErrorParamCategoriasMp(e?.message || 'Não foi possível carregar as categorias.');
          setParamCategoriasMp([]);
        }
      } finally {
        if (!cancelled) setLoadingParamCategoriasMp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isGlobal, tipoInsumo]);

  // Debug: Log quando formData.parametros muda
  useEffect(() => {
    console.log('📊 FormData.parametros atualizado:', formData.parametros);
    console.log('📊 Quantidade de parâmetros:', formData.parametros.length);
    if (formData.parametros.length > 0) {
      console.log('📊 Primeiro parâmetro:', formData.parametros[0]);
      console.log('📊 Campos do primeiro parâmetro:', Object.keys(formData.parametros[0]));
    }
  }, [formData.parametros]);

  // Formatar número decimal para exibição em pt-BR (vírgula como separador)
  const formatarDecimalBR = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return value;
    return num.toString().replace('.', ',');
  };

  // Ordem padrão dos campos para exibição consistente
  const ordemCampos = [
    'unidade',
    'valor_esperado', 
    'valor_maximo', 
    'valor_minimo',
    'limite_maximo', 
    'limite_minimo',
    'tipo_limite',
    'obrigatorio',
    'referencia',
    'base_correcao',
    'observacao',
    'referencia_normativa'
  ];

  // Função para formatar nome do campo para exibição
  const formatarNomeCampo = (campo) => {
    const mapeamento = {
      'nome': 'Nome',
      'unidade': 'Unidade',
      'limite_maximo': 'Limite Máximo',
      'limite_minimo': 'Limite Mínimo',
      'valor_maximo': 'Valor Máximo',
      'valor_minimo': 'Valor Mínimo',
      'valor_esperado': 'Valor Esperado',
      'tipo_limite': 'Tipo de Limite',
      'obrigatorio': 'Obrigatório',
      'referencia': 'Referência',
      'observacao': 'Observação',
      'base_correcao': 'Base de Correção',
      'referencia_normativa': 'Referência Normativa'
    };
    
    return mapeamento[campo] || campo.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Função para determinar cor do campo baseado no nome
  const getCorCampo = (campo) => {
    if (campo.includes('maximo') || campo.includes('máximo')) {
      return 'bg-red-50 text-red-600 border-red-200';
    }
    if (campo.includes('minimo') || campo.includes('mínimo')) {
      return 'bg-green-50 text-green-600 border-green-200';
    }
    if (campo.includes('esperado')) {
      return 'bg-blue-50 text-blue-600 border-blue-200';
    }
    if (campo.includes('unidade')) {
      return 'bg-gray-50 text-gray-600 border-gray-200';
    }
    if (campo.includes('referencia')) {
      return 'bg-purple-50 text-purple-600 border-purple-200';
    }
    if (campo.includes('obrigatorio')) {
      return 'bg-orange-50 text-orange-600 border-orange-200';
    }
    if (campo.includes('tipo')) {
      return 'bg-indigo-50 text-indigo-600 border-indigo-200';
    }
    return 'bg-yellow-50 text-yellow-600 border-yellow-200';
  };

  // Função para formatar valor para exibição
  const formatarValor = (valor) => {
    if (valor === null || valor === undefined) {
      return 'Não definido';
    }
    if (typeof valor === 'boolean') {
      return valor ? 'Sim' : 'Não';
    }
    if (typeof valor === 'number') {
      return valor.toString();
    }
    if (typeof valor === 'string') {
      return valor || 'Não definido';
    }
    return JSON.stringify(valor);
  };

  // Função para verificar se campo deve ser exibido
  const deveExibirCampo = (campo, valor) => {
    // NUNCA exibir o campo 'nome' (redundante com header)
    if (campo === 'nome') return false;
    
    // Não exibir campos vazios
    if (valor === null || valor === undefined || valor === '') {
      return false;
    }
    
    // Exibir todos os outros campos com valor
    return true;
  };

  // Função para ordenar campos de forma consistente
  const ordenarCampos = (campos) => {
    return campos.sort((a, b) => {
      const indexA = ordemCampos.indexOf(a);
      const indexB = ordemCampos.indexOf(b);
      
      // Se ambos estão na ordem definida, usar a ordem
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // Se apenas um está na ordem, priorizar o que está
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // Se nenhum está na ordem, ordem alfabética
      return a.localeCompare(b);
    });
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.categoria.trim()) {
      newErrors.categoria = 'Categoria é obrigatória';
    }

    if (!formData.industria.trim()) {
      newErrors.industria = 'Indústria é obrigatória';
    }

    if (!formData.unidade_medida.trim()) {
      newErrors.unidade_medida = 'Unidade de medida é obrigatória';
    }

    if (!isGlobal) {
      const em =
        formData.estoque_minimo === '' || formData.estoque_minimo === null
          ? NaN
          : Number(formData.estoque_minimo);
      if (!Number.isFinite(em) || em < 0) {
        newErrors.estoque_minimo = 'Informe um estoque mínimo válido (≥ 0)';
      }
    }

    // Validar parâmetros
    if (formData.parametros && formData.parametros.length > 0) {
      formData.parametros.forEach((param, index) => {
        if (!param.nome || !param.nome.trim()) {
          newErrors[`parametro_${index}_nome`] = `Parâmetro ${index + 1}: Nome é obrigatório`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar produto
  const handleSave = async () => {
    if (precisaVerificacaoMp && !cadastroMpDesbloqueado) {
      setActiveTab('verificacao');
      return;
    }

    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    if (!formData.categoria.trim()) {
      newErrors.categoria = 'Categoria é obrigatória';
    }

    if (tipoInsumo === 'servico') {
      if (!String(formData.frequencia_execucao || '').trim()) {
        newErrors.frequencia_execucao = 'Informe a frequência de execução';
      }
    } else {
      if (!formData.industria.trim()) {
        newErrors.industria = 'Indústria é obrigatória';
      }
      if (!formData.unidade_medida.trim()) {
        newErrors.unidade_medida = 'Unidade de medida é obrigatória';
      }
      if (!isGlobal) {
        const em =
          formData.estoque_minimo === '' || formData.estoque_minimo === null
            ? NaN
            : Number(formData.estoque_minimo);
        if (!Number.isFinite(em) || em < 0) {
          newErrors.estoque_minimo = 'Informe um estoque mínimo válido (≥ 0)';
        }
      }
    }
    
    // Validar parâmetros
    if (tipoInsumo !== 'servico' && formData.parametros && formData.parametros.length > 0) {
      formData.parametros.forEach((param, index) => {
        if (!param.nome || !param.nome.trim()) {
          newErrors[`parametro_${index}_nome`] = `Parâmetro ${index + 1}: Nome é obrigatório`;
        }
      });
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      // Se houver erros em parâmetros, ir para a aba de parâmetros
      const hasParamErrors = Object.keys(newErrors).some(key => key.startsWith('parametro_'));
      if (hasParamErrors) {
        setActiveTab('parametros');
      } else {
        setActiveTab('basico');
      }
      return;
    }

    setLoading(true);
    try {
      // Converter dados do formulário para o formato esperado
      let dataToSave = { ...formData };
      
      if (isGlobal) {
        // Produto da base global (produtos_regulamais)
        dataToSave = {
          nome_produto: formData.nome,
          codigo_produto: formData.codigo || undefined,
          categoria: formData.categoria,
          subcategoria: formData.subcategoria,
          industria: formData.industria,
          unidade_medida: formData.unidade_medida,
          fonte: formData.fonte || 'Manual',
          referencia_normativa: formData.referencia_normativa || undefined,
          observacoes_gerais: formData.observacoes 
            ? (typeof formData.observacoes === 'string' 
                ? formData.observacoes.split('\n').filter(line => line.trim())
                : formData.observacoes)
            : [],
          sinonimos: formData.sinonimos || [],
          notas: formData.notas 
            ? (typeof formData.notas === 'string' 
                ? formData.notas.split('\n').filter(line => line.trim())
                : formData.notas)
            : [],
          parametros_qualidade: formData.parametros || [] // ✅ NORMALIZADO: Sempre usar parametros_qualidade
        };
      } else {
        // Produto da empresa: garantir que parametros_qualidade vá para o backend
        const em = Number(formData.estoque_minimo);
        dataToSave = {
          ...formData,
          estoque_minimo: Number.isFinite(em) && em >= 0 ? em : 0,
          parametros_qualidade:
            tipoInsumo === 'servico' ? [] : formData.parametros || [],
          tipo_insumo: tipoInsumo,
          ...(tipoInsumo === 'servico'
            ? { frequencia_execucao: String(formData.frequencia_execucao || '').trim() }
            : {}),
        };
      }
      
      await onSave(sanitizeProductSavePayload(dataToSave, { isGlobal }));
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
    } finally {
      setLoading(false);
    }
  };

  // Adicionar sinônimo
  const addSinonimo = () => {
    const s = sanitizeSingleLineText(newSinonimo);
    if (s && !formData.sinonimos.includes(s)) {
      setFormData(prev => ({
        ...prev,
        sinonimos: [...prev.sinonimos, s]
      }));
      setNewSinonimo('');
    }
  };

  // Remover sinônimo
  const removeSinonimo = (index) => {
    setFormData(prev => ({
      ...prev,
      sinonimos: prev.sinonimos.filter((_, i) => i !== index)
    }));
  };

  // Adicionar novo parâmetro
  const addParametro = () => {
    const novoParametro = {
      padrao: 'Químico', // ✅ NOVO: Campo padrão (categoria)
      nome: '',
      unidade: '',
      tipo_limite: 'máximo',
      valor_minimo: null,
      valor_maximo: null,
      valor_esperado: '',
      base_correcao: '',
      obrigatorio: true,
      referencia: '',
      observacao: ''
    };
    setFormData(prev => ({
      ...prev,
      parametros: [...prev.parametros, novoParametro]
    }));
  };

  // Remover parâmetro
  const removeParametro = (index) => {
    setFormData(prev => ({
      ...prev,
      parametros: prev.parametros.filter((_, i) => i !== index)
    }));
  };

  // Atualizar campo de um parâmetro
  const updateParametro = (index, campo, valor) => {
    const v =
      campo === 'nome' && typeof valor === 'string'
        ? onChangeSingleLineText(valor)
        : valor;
    setFormData(prev => {
      const novosParametros = [...prev.parametros];
      novosParametros[index] = {
        ...novosParametros[index],
        [campo]: v
      };
      return {
        ...prev,
        parametros: novosParametros
      };
    });
  };

  const buscarSimilaresMp = async () => {
    const q = textoDescricaoMp.trim();
    if (q.length < 3) {
      setErroSimilares('Digite pelo menos 3 caracteres para buscar.');
      return;
    }
    setErroSimilares('');
    setLoadingSimilares(true);
    setSimilaresMp([]);
    try {
      const listProducts = httpsCallable(functions, 'listProducts');
      const empresaId = getEmpresaId();
      if (!empresaId || empresaId === 'default') {
        setErroSimilares('Empresa não identificada. Faça login novamente.');
        return;
      }
      const result = await listProducts({
        empresa_id: empresaId,
        page: 1,
        limit: 800,
        ativo: true
      });
      const raw = result.data?.data?.products || result.data?.data?.items || [];
      const lista = Array.isArray(raw) ? raw : [];
      const norm = (t) => (!t || t === 'materia_prima' ? 'ingrediente' : t);
      const mesmoTipo = lista.filter((p) => norm(p.tipo_insumo) === norm(tipoInsumo));
      const scored = mesmoTipo
        .map((p) => {
          const { score, matchTexto } = scoreSimilaridadeInsumo(q, p);
          return { ...p, _score: score, _matchTexto: matchTexto };
        })
        .filter((p) => p._score >= 0.22)
        .sort((a, b) => b._score - a._score)
        .slice(0, 25);
      setSimilaresMp(scored);
      setConfirmacaoNovaMp(false);
    } catch (e) {
      console.error(e);
      setErroSimilares(labels.verificacaoErroBusca);
    } finally {
      setLoadingSimilares(false);
    }
  };

  const continuarCadastroAposVerificacao = () => {
    if (!confirmacaoNovaMp) return;
    const nomeSugerido = textoDescricaoMp.trim();
    if (nomeSugerido) {
      setFormData((prev) => ({ ...prev, nome: nomeSugerido }));
    }
    setCadastroMpDesbloqueado(true);
    setActiveTab('basico');
  };

  // Renderizar aba de verificação (evitar duplicatas)
  const renderVerificacaoTab = () => (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Antes de cadastrar</p>
          <p className="mt-1 text-amber-800">{labels.verificacaoIntro}</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{labels.verificacaoLabel}</label>
        <textarea
          value={textoDescricaoMp}
          onChange={(e) => {
            setTextoDescricaoMp(onChangeMultiLineText(e.target.value));
            setConfirmacaoNovaMp(false);
          }}
          onBlur={() => setTextoDescricaoMp(sanitizeMultiLineText(textoDescricaoMp))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={labels.placeholderVerificacao}
        />
        <p className="text-xs text-gray-500 mt-1">Use o nome como você costuma chamar o item na operação.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={buscarSimilaresMp}
          disabled={loadingSimilares || textoDescricaoMp.trim().length < 3}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {loadingSimilares ? (
            <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Buscar cadastros parecidos
        </button>
      </div>

      {erroSimilares && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{erroSimilares}</div>
      )}

      {similaresMp.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            {labels.verificacaoListaTitulo} ({similaresMp.length})
          </h4>
          <p className="text-xs text-gray-600 mb-3">
            Confira se alguma delas não é a mesma que você precisa. Se for, cancele e use o cadastro existente.
          </p>
          <ul className="max-h-52 overflow-y-auto space-y-2">
            {similaresMp.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-baseline justify-between gap-2 rounded-md bg-white border border-gray-100 px-3 py-2 text-sm"
              >
                <span className="font-medium text-gray-900">{p.nome}</span>
                <span className="text-xs text-gray-500">
                  {p._matchTexto &&
                    normalizarTextoMp(p._matchTexto) !== normalizarTextoMp(p.nome || '') && (
                      <span className="mr-2 text-amber-700">Sinônimo: {p._matchTexto}</span>
                    )}
                  {p.categoria && <span className="mr-2">Cat.: {p.categoria}</span>}
                  {p.codigo && <span>Cód.: {p.codigo}</span>}
                  <span className="ml-2 text-blue-600">
                    similaridade {(p._score * 100).toFixed(0)}%
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loadingSimilares && textoDescricaoMp.trim().length >= 3 && similaresMp.length === 0 && !erroSimilares && (
        <p className="text-sm text-gray-600">{labels.verificacaoSemResultado}</p>
      )}

      <div className="border-t border-gray-200 pt-4 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={confirmacaoNovaMp}
            onChange={(e) => setConfirmacaoNovaMp(e.target.checked)}
          />
          <span className="text-sm text-gray-800">
            Confirmo que revisei os resultados (ou busquei por semelhança) e{' '}
            <strong>{labels.verificacaoConfirmacao}</strong>, e não usar uma já existente.
          </span>
        </label>
        <button
          type="button"
          onClick={continuarCadastroAposVerificacao}
          disabled={!confirmacaoNovaMp}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          <ClipboardCheck className="h-4 w-4" />
          Continuar para aba Básico e demais campos
        </button>
      </div>
    </div>
  );

  const tabItems = useMemo(() => {
    if (tipoInsumo === 'servico') {
      return [
        { id: 'basico', label: 'Dados do serviço', icon: Package },
        { id: 'notas', label: 'Observações internas', icon: FileText },
      ];
    }
    const core = [
      { id: 'basico', label: 'Básico', icon: Package },
      { id: 'sinonimos', label: `Sinônimos (${formData.sinonimos.length})`, icon: Tag },
      { id: 'parametros', label: `Parâmetros (${formData.parametros.length})`, icon: CheckCircle },
      { id: 'notas', label: 'Notas', icon: FileText }
    ];
    if (precisaVerificacaoMp) {
      return [{ id: 'verificacao', label: labels.verificacaoTab, icon: Search }, ...core];
    }
    return core;
  }, [
    tipoInsumo,
    precisaVerificacaoMp,
    formData.sinonimos.length,
    formData.parametros.length,
    labels.verificacaoTab,
  ]);

  // Renderizar aba básica
  const renderBasicTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {labels.nomeCampo} *
          </label>
          <input
            type="text"
            value={formData.nome}
            onChange={patchSingleLine('nome')}
            onBlur={blurSingleLine('nome')}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = (e.clipboardData.getData('text') || '').replace(/[\r\n]+/g, ' ');
              const el = e.target;
              const start = el.selectionStart ?? 0;
              const end = el.selectionEnd ?? 0;
              const next =
                onChangeSingleLineText(formData.nome.slice(0, start)) +
                pasted +
                formData.nome.slice(end);
              setFormData((prev) => ({
                ...prev,
                nome: onChangeSingleLineText(next),
              }));
            }}
            disabled={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.nome ? 'border-red-500' : 'border-gray-300'
            } ${mode === 'view' ? 'bg-gray-50' : ''}`}
            placeholder={labels.placeholderNome}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="text-xs text-gray-500 mt-1">
            Não use espaço ou quebra de linha no início do nome.
          </p>
          {errors.nome && (
            <p className="text-red-500 text-xs mt-1">{errors.nome}</p>
          )}
        </div>

        {!isGlobal ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              {loadingParamCategoriasMp ? (
                <p className="text-sm text-gray-500 py-2">Carregando categorias parametrizadas...</p>
              ) : (
                <>
                  <select
                    value={formData.categoria}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({ ...prev, categoria: v, subcategoria: '' }));
                    }}
                    disabled={mode === 'view'}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                      errors.categoria ? 'border-red-500' : 'border-gray-300'
                    } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                  >
                    <option value="">Selecione a categoria</option>
                    {paramCategoriasMp.map((row) => (
                      <option key={`cat-${row.categoria}`} value={row.categoria}>
                        {row.categoria}
                      </option>
                    ))}
                    {formData.categoria &&
                      !paramCategoriasMp.some((r) => r.categoria === formData.categoria) && (
                        <option value={formData.categoria}>
                          {formData.categoria} (valor atual — revise em Parâmetros)
                        </option>
                      )}
                  </select>
                  {errors.categoria && (
                    <p className="text-red-500 text-xs mt-1">{errors.categoria}</p>
                  )}
                  {errorParamCategoriasMp && (
                    <p className="text-red-500 text-xs mt-1">{errorParamCategoriasMp}</p>
                  )}
                  {!loadingParamCategoriasMp &&
                    paramCategoriasMp.length === 0 &&
                    !errorParamCategoriasMp && (
                      <p className="text-amber-800 text-xs mt-1">
                        Nenhuma categoria cadastrada para {labels.categoriasAvisoTipo}. O superadmin pode cadastrar em{' '}
                        <strong>Parâmetros → Categorias de Insumos</strong> ({labels.categoriasAvisoParam}).
                      </p>
                    )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategoria <span className="font-normal text-gray-500">(opcional)</span>
              </label>
              <select
                value={formData.subcategoria || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subcategoria: e.target.value }))
                }
                disabled={mode === 'view' || !formData.categoria}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                  mode === 'view' ? 'bg-gray-50' : ''
                }`}
              >
                <option value="">Nenhuma</option>
                {subcategoriasOpcoesMp.map((s) => (
                  <option key={`sub-${s}`} value={s}>
                    {s}
                  </option>
                ))}
                {formData.subcategoria &&
                  !subcategoriasOpcoesMp.includes(formData.subcategoria) && (
                    <option value={formData.subcategoria}>
                      {formData.subcategoria} (valor atual)
                    </option>
                  )}
              </select>
              {formData.categoria && subcategoriasOpcoesMp.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Esta categoria não possui subcategorias cadastradas; você pode deixar em &quot;Nenhuma&quot;.
                </p>
              )}
            </div>

            {tipoInsumo === 'servico' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    Frequência de execução *
                  </span>
                </label>
                <select
                  value={formData.frequencia_execucao || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      frequencia_execucao: e.target.value,
                    }))
                  }
                  disabled={mode === 'view'}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                    errors.frequencia_execucao ? 'border-red-500' : 'border-gray-300'
                  } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                >
                  <option value="">Selecione a periodicidade</option>
                  {FREQUENCIA_EXECUCAO_OPCOES.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Serviços periódicos ligados à qualidade e boas práticas na fábrica.
                </p>
                {errors.frequencia_execucao && (
                  <p className="text-red-500 text-xs mt-1">{errors.frequencia_execucao}</p>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <input
                type="text"
                value={formData.categoria}
                onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                disabled={mode === 'view'}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.categoria ? 'border-red-500' : 'border-gray-300'
                } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                placeholder="Ex: Origem animal, Origem vegetal"
              />
              {errors.categoria && (
                <p className="text-red-500 text-xs mt-1">{errors.categoria}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategoria
              </label>
              <input
                type="text"
                value={formData.subcategoria}
                onChange={(e) => setFormData(prev => ({ ...prev, subcategoria: e.target.value }))}
                disabled={mode === 'view'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  mode === 'view' ? 'bg-gray-50' : ''
                }`}
                placeholder="Ex: Farinhas de carne, Cereais"
              />
            </div>
          </>
        )}

        {tipoInsumo !== 'servico' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Indústria *
          </label>
          <select
            value={formData.industria}
            onChange={(e) => setFormData(prev => ({ ...prev, industria: e.target.value }))}
            disabled={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.industria ? 'border-red-500' : 'border-gray-300'
            } ${mode === 'view' ? 'bg-gray-50' : ''}`}
          >
            <option value="">Selecione a indústria</option>
            <option value="petfood">Pet Food</option>
            <option value="humana">Alimentação Humana</option>
            <option value="aquicultura">Aquicultura</option>
            <option value="suinocultura">Suinocultura</option>
            <option value="avicultura">Avicultura</option>
            <option value="bovinocultura">Bovinocultura</option>
          </select>
          {errors.industria && (
            <p className="text-red-500 text-xs mt-1">{errors.industria}</p>
          )}
        </div>
        )}

        {tipoInsumo !== 'servico' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unidade de medida *
          </label>
          <input
            type="text"
            value={formData.unidade_medida}
            onChange={(e) => setFormData(prev => ({ ...prev, unidade_medida: e.target.value }))}
            disabled={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.unidade_medida ? 'border-red-500' : 'border-gray-300'
            } ${mode === 'view' ? 'bg-gray-50' : ''}`}
            placeholder="Ex: kg, L, unidade"
          />
          {errors.unidade_medida && (
            <p className="text-red-500 text-xs mt-1">{errors.unidade_medida}</p>
          )}
        </div>
        )}

        {!isGlobal && tipoInsumo !== 'servico' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estoque mínimo
            </label>
            <input
              type="number"
              min="0"
              step="any"
              value={formData.estoque_minimo === '' || formData.estoque_minimo === null ? '' : formData.estoque_minimo}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  setFormData((prev) => ({ ...prev, estoque_minimo: '' }));
                  return;
                }
                const n = parseFloat(v);
                setFormData((prev) => ({
                  ...prev,
                  estoque_minimo: Number.isFinite(n) ? n : prev.estoque_minimo
                }));
              }}
              disabled={mode === 'view'}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.estoque_minimo ? 'border-red-500' : 'border-gray-300'
              } ${mode === 'view' ? 'bg-gray-50' : ''}`}
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Quantidade mínima desejada em estoque (usa a mesma unidade de medida acima).
            </p>
            {errors.estoque_minimo && (
              <p className="text-red-500 text-xs mt-1">{errors.estoque_minimo}</p>
            )}
          </div>
        )}

        {!isGlobal && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              disabled={mode === 'view'}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                mode === 'view' ? 'bg-gray-50' : ''
              }`}
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="pendente">Pendente</option>
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Observações Gerais
        </label>
        <textarea
          value={formData.observacoes}
          onChange={patchMultiLine('observacoes')}
          onBlur={blurMultiLine('observacoes')}
          disabled={mode === 'view'}
          rows={3}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            mode === 'view' ? 'bg-gray-50' : ''
          }`}
          placeholder={labels.obsPlaceholder}
        />
      </div>

      {/* Informações adicionais para produtos globais */}
      {isGlobal && product && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-5 h-5 text-blue-500" />
            <h4 className="font-medium text-blue-900">Informações ABINPET</h4>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
            {product.codigo_produto && (
              <div>
                <span className="font-medium">Código:</span> {product.codigo_produto}
              </div>
            )}
            {product.fonte && (
              <div>
                <span className="font-medium">Fonte:</span> {product.fonte}
              </div>
            )}
            {product.versao && (
              <div>
                <span className="font-medium">Versão:</span> {product.versao}
              </div>
            )}
            {product.referencia_normativa && (
              <div>
                <span className="font-medium">Referência:</span> {product.referencia_normativa}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Renderizar aba de sinônimos
  const renderSynonymsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="w-5 h-5 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900">Sinônimos e Nomes Alternativos</h3>
      </div>

      {mode !== 'view' && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newSinonimo}
            onChange={(e) => setNewSinonimo(onChangeSingleLineText(e.target.value))}
            onBlur={() => setNewSinonimo(sanitizeSingleLineText(newSinonimo))}
            onKeyPress={(e) => e.key === 'Enter' && addSinonimo()}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Digite um sinônimo..."
          />
          <button
            onClick={addSinonimo}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-2">
        {formData.sinonimos.length === 0 ? (
          <p className="text-gray-500 text-center py-4">Nenhum sinônimo cadastrado</p>
        ) : (
          formData.sinonimos.map((sinonimo, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
              <span className="text-gray-900">{sinonimo}</span>
              {mode !== 'view' && (
                <button
                  onClick={() => removeSinonimo(index)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Renderizar aba de parâmetros (TABELA EDITÁVEL)
  const renderParametersTab = () => {
    const isViewMode = mode === 'view';
    const canEdit = mode !== 'view';
    
    // Função auxiliar para determinar tipo_limite baseado nos valores
    const getTipoLimite = (param) => {
      if (param.tipo_limite === 'descritivo' || (!param.valor_minimo && !param.valor_maximo && param.tipo_limite !== 'máximo' && param.tipo_limite !== 'mínimo')) {
        return 'descritivo';
      }
      if (param.valor_minimo && param.valor_maximo) {
        return 'faixa';
      }
      if (param.valor_minimo) {
        return 'mínimo';
      }
      return param.tipo_limite || 'máximo';
    };

    // Função para atualizar tipo_limite e limpar valores quando necessário
    const handleTipoLimiteChange = (index, newTipo) => {
      const param = formData.parametros[index];
      if (newTipo === 'descritivo') {
        updateParametro(index, 'tipo_limite', 'descritivo');
        updateParametro(index, 'valor_minimo', null);
        updateParametro(index, 'valor_maximo', null);
      } else {
        updateParametro(index, 'tipo_limite', newTipo);
      }
    };
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">
              Parâmetros de Qualidade ({formData.parametros.length})
            </h3>
          </div>
          {canEdit && (
            <button
              onClick={addParametro}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Adicionar Parâmetro
            </button>
          )}
        </div>

        {formData.parametros.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Nenhum parâmetro cadastrado</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Padrão</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Descrição</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Valor Esperado</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Unidade</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Tipo de Limite</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Valor Mínimo</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Valor Máximo</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Obrigatório</th>
                  {canEdit && (
                    <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Ações</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {formData.parametros.map((parametro, index) => {
                  const tipoLimite = getTipoLimite(parametro);
                  const isDescritivo = tipoLimite === 'descritivo';
                  
                  return (
                    <tr key={`param-${index}`} className="hover:bg-gray-50">
                      {/* Padrão */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <select
                            value={parametro.padrao || 'Químico'}
                            onChange={(e) => updateParametro(index, 'padrao', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="Microbiológico">Microbiológico</option>
                            <option value="Químico">Químico</option>
                            <option value="Sensorial">Sensorial</option>
                            <option value="Físico">Físico</option>
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900">{parametro.padrao || 'Químico'}</span>
                        )}
                      </td>
                      
                      {/* Descrição */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <input
                            type="text"
                            value={parametro.nome || ''}
                            onChange={(e) => updateParametro(index, 'nome', e.target.value)}
                            onBlur={() => {
                              const n = sanitizeSingleLineText(formData.parametros[index]?.nome || '');
                              updateParametro(index, 'nome', n);
                            }}
                            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                              errors[`parametro_${index}_nome`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Nome do parâmetro"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">{parametro.nome || '-'}</span>
                        )}
                        {errors[`parametro_${index}_nome`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`parametro_${index}_nome`]}</p>
                        )}
                      </td>
                      
                      {/* Valor Esperado */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <input
                            type="text"
                            value={parametro.valor_esperado || ''}
                            onChange={(e) => updateParametro(index, 'valor_esperado', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Ex: 1x10³, 12%, Pastosa"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">
                            {(typeof parametro.valor_esperado === 'number') || (typeof parametro.valor_esperado === 'string' && /^-?\d*\.?\d+$/.test(String(parametro.valor_esperado).trim()))
                              ? formatarDecimalBR(parametro.valor_esperado)
                              : (parametro.valor_esperado || '-')}
                          </span>
                        )}
                      </td>
                      
                      {/* Unidade */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <input
                            type="text"
                            value={parametro.unidade || ''}
                            onChange={(e) => updateParametro(index, 'unidade', e.target.value)}
                            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                              errors[`parametro_${index}_unidade`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="UFC/g, %, cm"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">{parametro.unidade || '-'}</span>
                        )}
                        {errors[`parametro_${index}_unidade`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`parametro_${index}_unidade`]}</p>
                        )}
                      </td>
                      
                      {/* Tipo de Limite */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <select
                            value={tipoLimite}
                            onChange={(e) => handleTipoLimiteChange(index, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="máximo">Máximo</option>
                            <option value="mínimo">Mínimo</option>
                            <option value="faixa">Faixa</option>
                            <option value="descritivo">Descritivo</option>
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900 capitalize">{tipoLimite}</span>
                        )}
                      </td>
                      
                      {/* Valor Mínimo */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <input
                            type="number"
                            step="any"
                            value={parametro.valor_minimo ?? ''}
                            onChange={(e) => updateParametro(index, 'valor_minimo', e.target.value === '' ? null : parseFloat(e.target.value))}
                            disabled={isDescritivo}
                            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                              isDescritivo ? 'bg-gray-100 text-gray-400' : 'border-gray-300'
                            }`}
                            placeholder="-"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">{formatarDecimalBR(parametro.valor_minimo)}</span>
                        )}
                      </td>
                      
                      {/* Valor Máximo */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <input
                            type="number"
                            step="any"
                            value={parametro.valor_maximo ?? ''}
                            onChange={(e) => updateParametro(index, 'valor_maximo', e.target.value === '' ? null : parseFloat(e.target.value))}
                            disabled={isDescritivo}
                            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                              isDescritivo ? 'bg-gray-100 text-gray-400' : 'border-gray-300'
                            }`}
                            placeholder="-"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">{formatarDecimalBR(parametro.valor_maximo)}</span>
                        )}
                      </td>
                      
                      {/* Obrigatório */}
                      <td className="border border-gray-300 px-3 py-2">
                        {canEdit ? (
                          <select
                            value={parametro.obrigatorio ? 'Sim' : 'Não'}
                            onChange={(e) => updateParametro(index, 'obrigatorio', e.target.value === 'Sim')}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900">{parametro.obrigatorio ? 'Sim' : 'Não'}</span>
                        )}
                      </td>
                      
                      {/* Ações */}
                      {canEdit && (
                        <td className="border border-gray-300 px-3 py-2">
                          <button
                            onClick={() => removeParametro(index)}
                            className="text-red-500 hover:text-red-700 transition-colors p-1"
                            title="Remover parâmetro"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Renderizar aba de notas
  const renderNotesTab = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900">Notas e Informações Técnicas</h3>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notas Técnicas
        </label>
        <textarea
          value={formData.notas}
          onChange={patchMultiLine('notas')}
          onBlur={blurMultiLine('notas')}
          disabled={mode === 'view'}
          rows={8}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            mode === 'view' ? 'bg-gray-50' : ''
          }`}
          placeholder="Informações técnicas, especificações, regulamentações aplicáveis, etc..."
        />
      </div>

      {/* Fontes bibliográficas para produtos globais */}
      {isGlobal && product && product.fontes_bibliograficas && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-green-500" />
            <h4 className="font-medium text-green-900">Fontes Bibliográficas</h4>
          </div>
          <div className="space-y-1">
            {product.fontes_bibliograficas.map((fonte, index) => (
              <div key={index} className="text-sm text-green-800">
                • {fonte}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {mode === 'view' ? (
              <Eye className="w-6 h-6 text-blue-500" />
            ) : mode === 'edit' ? (
              <Edit className="w-6 h-6 text-green-500" />
            ) : (
              <Plus className="w-6 h-6 text-purple-500" />
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {mode === 'view' ? labels.verItem :
                 mode === 'edit' ? labels.editarItem : labels.novoItem}
              </h2>
              {product && (
                <p className="text-sm text-gray-600">
                  {isGlobal ? product.nome_produto : product.nome}
                  {isGlobal && product.codigo_produto && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {product.codigo_produto}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap gap-x-6 gap-y-1 px-6">
            {tabItems.map(({ id, label, icon: Icon }) => {
              const bloqueado =
                precisaVerificacaoMp && !cadastroMpDesbloqueado && id !== 'verificacao';
              return (
                <button
                  key={id}
                  type="button"
                  disabled={bloqueado}
                  title={bloqueado ? labels.tabBloqueadaHint : undefined}
                  onClick={() => {
                    if (bloqueado) return;
                    setActiveTab(id);
                  }}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                    bloqueado
                      ? 'border-transparent text-gray-300 cursor-not-allowed'
                      : activeTab === id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'verificacao' && precisaVerificacaoMp && renderVerificacaoTab()}
          {activeTab === 'basico' && renderBasicTab()}
          {activeTab === 'sinonimos' && renderSynonymsTab()}
          {activeTab === 'parametros' && renderParametersTab()}
          {activeTab === 'notas' && renderNotesTab()}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
          >
            {mode === 'view' ? 'Fechar' : 'Cancelar'}
          </button>
          
          {mode !== 'view' && (
            <button
              onClick={handleSave}
              disabled={
                loading ||
                (precisaVerificacaoMp && !cadastroMpDesbloqueado)
              }
              title={
                precisaVerificacaoMp && !cadastroMpDesbloqueado
                  ? 'Conclua a verificação na primeira aba antes de salvar.'
                  : undefined
              }
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <Save className="w-4 h-4" />
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductModal;

